// Who may run a gear check, and who may edit its template.
//
// Both questions used to be answered only by firestore.rules, so the UI offered
// buttons that blew up with a raw permission-denied snackbar. These helpers are
// the UI half of the same gate — keep them in step with the `/{path=**}/checks`
// and `/checks/{id}` rules.
import {hasPermission} from '-/data/utils';

// Running a check (start / continue / autosave) writes `.../checks/{id}` under
// the gear item. The rule allows `records.write` OR `gearChecks.write`, and the
// role ladder hands both out together at Gear = Edit — but `records.write` also
// arrives via Apparatus = Edit, so mirroring the OR here keeps the button in
// front of everyone the rules would actually let through.
export const canWriteGearChecks = user =>
    hasPermission(user, 'gearChecks.write') === true || hasPermission(user, 'records.write') === true;

// Adding or editing a check TEMPLATE writes the top-level `/checks/{id}` doc
// (plus the `checks.<type>` flag on the gear category, which needs gear.write —
// the same Gear = Approve rung that grants gearChecks.approve). `editChecks.write`
// is the apparatus-side claim and still works; it is not on the gear ladder.
export const canEditGearCheckTemplates = user =>
    hasPermission(user, 'editChecks.write') === true || hasPermission(user, 'gearChecks.approve') === true;

// Authoring a HALL checklist (#1920). `editChecks.write` is the Apparatus
// area's claim and still works; the Stations ladder hands out
// `stationChecks.approve` at Approve instead, which firestore.rules accepts for
// a template carrying `stations` — so a station approver can write its own
// checklists without gaining the ability to rewrite apparatus ones.
export const canEditStationCheckTemplates = user =>
    hasPermission(user, 'editChecks.write') === true || hasPermission(user, 'stationChecks.approve') === true;

// The claims that run and delete a check, by the collection it lives under.
// Gear accepts either claim of each pair, as its rules do.
const CHECK_CLAIMS = {
    apparatus: {write: ['apparatusChecks.write'], delete: ['apparatusChecks.delete']},
    gear: {write: ['gearChecks.write', 'records.write'], delete: ['gearChecks.delete', 'records.delete']},
    stations: {write: ['stationChecks.write'], delete: ['stationChecks.delete']}
};

const memberIdOf = member => typeof member === 'string' ? member : (member?.uid || member?.id);

// Deleting a check — including cancelling an in-progress one, which deletes it
// (#996). The UI half of the check delete rules in firestore.rules: the delete
// claim always may; otherwise the check must still be active, and the user must
// hold the claim that runs it and either have started it (`createdBy`) or be
// signed on it (`users`, uids or populated user objects). `collectionKey` is
// the check's root collection: `apparatus`, `gear` or `stations`.
export const canDeleteCheck = (user, check, collectionKey) => {
    const claims = CHECK_CLAIMS[collectionKey];
    if (!user || !claims) {
        return false;
    }

    const holdsAny = keys => keys.some(key => hasPermission(user, key) === true);
    if (holdsAny(claims.delete)) {
        return true;
    }

    if (!check?.active || !user.uid || !holdsAny(claims.write)) {
        return false;
    }

    return check.createdBy === user.uid
        || (check.users || []).some(member => memberIdOf(member) === user.uid);
};
